import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ErrorCode, type ApiErrorBody, type TaskNextStep } from '@brimo/shared';
import { ApiError, GENERIC_ERROR_MESSAGE, api } from '../../lib/api';
import { queryRoots } from '../../lib/events';

/** Types and hooks of the self-disclosure routes (docs/dev/api-self-disclosure.md). */

export type SelfDisclosureStatus = 'draft' | 'sent' | 'in_progress' | 'submitted' | 'reviewed';

export interface SelfDisclosureRow {
  id: string;
  contactId: string;
  dealId: string | null;
  version: number;
  status: SelfDisclosureStatus;
  statusLabel: string;
  sentAt: string | null;
  sentBy: { id: string; name: string } | null;
  /** `referrer`: submitted by a referrer partner through `/t/<token>` (D-248), never sent. */
  sentVia: 'email' | 'whatsapp' | 'portal' | 'referrer' | null;
  /** The referrer partner of a version submitted through the partner's link (D-248). */
  referrerPartner: { id: string; name: string } | null;
  /** The referrer who submitted that version. */
  submittedBy: { name: string; email: string; phone: string | null; note: string | null } | null;
  expiresAt: string | null;
  expired: boolean;
  openedAt: string | null;
  currentStep: number;
  lastSavedAt: string | null;
  submittedAt: string | null;
  signaturePlace: string | null;
  followUpDays: number | null;
  followUpTaskId: string | null;
  volumeCents: number | null;
  pdfFileId: string | null;
  previousVersionId: string | null;
  changedFields: string[];
  changedCount: number;
  kindLabel: string;
}

export interface FieldRow {
  field: string;
  label: string;
  value: string;
}

export interface FullViewGroup {
  key: 'person' | 'employment' | 'income' | 'expenses' | 'assets' | 'consents';
  title: string;
  rows: FieldRow[];
}

export interface FullViewProof {
  id: string;
  type: string;
  typeLabel: string;
  fileName: string;
  size: number;
  mime: string;
  scanStatus: 'pending' | 'clean' | 'infected' | 'error';
  uploadedAt: string;
  fileId: string;
  status: 'uploaded' | 'reviewed';
  statusLabel: string;
  canOpen: boolean;
}

export interface SelfDisclosureEvent {
  type: string;
  title: string;
  at: string;
  by: string;
}

export interface VersionSummary {
  id: string;
  version: number;
  status: SelfDisclosureStatus;
  statusLabel: string;
  submittedAt: string | null;
  kindLabel: string;
}

export interface FullView {
  selfDisclosure: SelfDisclosureRow;
  contact: { id: string; name: string };
  headline: string;
  signature: { signedAt: string; place: string | null; fileId: string | null } | null;
  consents: {
    completenessAt: string | null;
    sharingAt: string | null;
    propertyInfoConsented: boolean;
    signedAt: string | null;
    place: string | null;
  } | null;
  groups: FullViewGroup[];
  proofs: FullViewProof[];
  events: SelfDisclosureEvent[];
  visibleTo: Array<{
    role: 'advisor' | 'financier' | 'admin';
    label: string;
    name: string | null;
    note: string | null;
  }>;
  visibilityNote: string;
  volume: {
    usedVolumeCents: number;
    computedVolumeCents: number;
    manual: boolean;
    calculatedAt: string;
    expiresAt: string | null;
    subline: string;
  } | null;
  canMarkReviewed: boolean;
  versions: VersionSummary[];
  pdfFileId: string | null;
}

export interface CompareRow {
  group: 'person' | 'income' | 'expenses' | 'assets';
  field: string;
  label: string;
  oldValue: string;
  newValue: string;
  changed: boolean;
}

export interface CompareResponse {
  version: VersionSummary;
  previous: VersionSummary;
  rows: CompareRow[];
  changedCount: number;
  volume: { before: number | null; after: number | null };
  financier: { id: string; name: string } | null;
}

export interface VolumeView {
  selfDisclosureId: string;
  version: number;
  calculatedAt: string;
  income: {
    net: number;
    other: number;
    rental: {
      gross: number;
      creditPct: number;
      credited: number;
      source: 'unit' | 'form';
      unitLabel: string | null;
    };
    countable: number;
  };
  charges: {
    livingExpenses: number;
    rent: number;
    loans: number;
    leasing: number;
    alimony: number;
    total: number;
    disposable: number;
  };
  service: {
    disposable: number;
    capPct: number;
    capLimit: number;
    affordableInstallment: number;
    annuityPct: number;
    computedVolume: number;
  };
  used: {
    volumeCents: number;
    manual: boolean;
    reason: string | null;
    byShort: string | null;
    at: string | null;
  };
  assumptions: {
    livingExpensesCents: number;
    rentCreditPct: number;
    capPct: number;
    interestPct: number;
    repaymentPct: number;
    frozenAt: string;
  };
  expiresAt: string | null;
}

export interface ReviewedResponse {
  selfDisclosure: SelfDisclosureRow;
  completedTaskId: string | null;
  createdTaskId: string | null;
  next: 'book_appointment' | null;
}

export const selfDisclosureKeys = {
  full: (id: string) => [...queryRoots.selfDisclosures, 'full', id] as const,
  compare: (id: string, withId: string | null) =>
    [...queryRoots.selfDisclosures, 'compare', id, withId] as const,
  volume: (id: string) => [...queryRoots.selfDisclosures, 'volume', id] as const,
};

const notRetried = (error: unknown) =>
  error instanceof ApiError && (error.status === 403 || error.status === 404 || error.status === 409);

export function useSelfDisclosureFull(id: string) {
  return useQuery({
    queryKey: selfDisclosureKeys.full(id),
    queryFn: ({ signal }) => api<FullView>(`/self-disclosures/${id}/full`, { signal }),
    retry: (count, error) => !notRetried(error) && count < 1,
  });
}

export function useSelfDisclosureCompare(id: string, withId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: selfDisclosureKeys.compare(id, withId),
    queryFn: ({ signal }) =>
      api<CompareResponse>(`/self-disclosures/${id}/compare${withId ? `?with=${withId}` : ''}`, { signal }),
    enabled,
    retry: (count, error) => !notRetried(error) && count < 1,
  });
}

export function useSelfDisclosureVolume(id: string, enabled: boolean) {
  return useQuery({
    queryKey: selfDisclosureKeys.volume(id),
    queryFn: ({ signal }) => api<VolumeView>(`/self-disclosures/${id}/volume`, { signal }),
    enabled,
    retry: (count, error) => !notRetried(error) && count < 1,
  });
}

export function useInvalidateSelfDisclosure() {
  const queryClient = useQueryClient();
  return async (): Promise<void> => {
    await Promise.all(
      [queryRoots.selfDisclosures, queryRoots.contact, queryRoots.tasks, queryRoots.session].map((key) =>
        queryClient.invalidateQueries({ queryKey: [...key] }),
      ),
    );
  };
}

export function markReviewed(id: string, nextStep?: TaskNextStep): Promise<ReviewedResponse> {
  return api<ReviewedResponse>(`/self-disclosures/${id}/reviewed`, {
    method: 'POST',
    body: nextStep === undefined ? {} : { nextStep },
  });
}

export function useManualVolume(id: string) {
  const invalidate = useInvalidateSelfDisclosure();
  return useMutation({
    mutationFn: (body: { usedVolumeCents: number; reason: string }) =>
      api<VolumeView>(`/self-disclosures/${id}/volume/manual`, { method: 'POST', body }),
    onSuccess: invalidate,
  });
}

export function useRevertManualVolume(id: string) {
  const invalidate = useInvalidateSelfDisclosure();
  return useMutation({
    mutationFn: () => api<VolumeView>(`/self-disclosures/${id}/volume/manual`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

export function useReportToFinancier(id: string) {
  const invalidate = useInvalidateSelfDisclosure();
  return useMutation({
    mutationFn: () =>
      api<{ taskId: string; financier: { id: string; name: string } }>(
        `/self-disclosures/${id}/report-to-financier`,
        {
          method: 'POST',
          body: {},
        },
      ),
    onSuccess: invalidate,
  });
}

// --- public form (`/api/public/self-disclosures/:token`) ------------------------------------------

export interface PublicAdvisor {
  name: string;
  email: string;
  phone: string | null;
  initials: string;
}

export interface PublicOrganization {
  name: string;
  privacyUrl: string | null;
}

export interface PersonStepValues {
  firstName: string;
  lastName: string;
  bornOn: string | null;
  maritalStatus: string | null;
  children: number | null;
  nationality: string | null;
  street: string | null;
  postalCode: string | null;
  city: string | null;
  phone: string | null;
  email: string | null;
}

export interface IncomeStepValues {
  employment: 'employed' | 'civil_servant' | 'self_employed' | 'retired';
  employer: string | null;
  employedSinceOn: string | null;
  contract: 'permanent' | 'fixed_term' | null;
  fixedTermUntilOn: string | null;
  netIncomeCents: number;
  salariesPerYear: number | null;
  specialPaymentsPaCents: number | null;
  otherIncomeCents: number | null;
  otherIncomeType: string | null;
  rentalIncomeCents: number | null;
}

export interface ExpensesStepValues {
  housingSituation: 'rent' | 'ownership' | 'parents';
  grossRentCents: number | null;
  rentContinues: boolean;
  loansCents: number | null;
  loansRemainingTerm: string | null;
  alimonyCents: number | null;
  privateHealthInsuranceCents: number | null;
  leasingCents: number | null;
}

export interface AssetsStepValues {
  equityCents: number | null;
  securitiesCents: number | null;
  otherCents: number | null;
}

/**
 * Prefill of the four steps from the contact's properties (owner decision 12.10.2026): the form shows a saved
 * step's values, else these. Nothing of the prefill counts as saved.
 */
export interface PublicPrefill {
  person: { [K in keyof PersonStepValues]: PersonStepValues[K] | null } & {
    firstName: string;
    lastName: string;
  };
  income: { [K in keyof IncomeStepValues]: IncomeStepValues[K] | null };
  expenses: { [K in keyof ExpensesStepValues]: ExpensesStepValues[K] | null };
  assets: { [K in keyof AssetsStepValues]: AssetsStepValues[K] | null };
}

export interface PublicProof {
  id: string;
  type: 'id_document' | 'salary_proof' | 'equity' | 'other';
  typeLabel: string;
  fileName: string;
  size: number;
  mime: string;
  scanStatus: 'pending' | 'clean' | 'infected' | 'error';
  uploadedAt: string;
}

export interface PublicState {
  state: 'open' | 'submitted';
  version: number;
  currentStep: number;
  lastSavedAt: string | null;
  expiresAt: string;
  correctionUntil: string | null;
  submittedAt: string | null;
  pdfAvailable: boolean;
  advisor: PublicAdvisor;
  organization: PublicOrganization;
  contact: {
    firstName: string;
    lastName: string;
    email: string | null;
    phone: string | null;
    street: string | null;
    postalCode: string | null;
    city: string | null;
    bornOn: string | null;
  };
  steps: {
    person: PersonStepValues | null;
    income: IncomeStepValues | null;
    expenses: ExpensesStepValues | null;
    assets: AssetsStepValues | null;
    consents: {
      completenessAt: string | null;
      sharingAt: string | null;
      propertyInfoConsented: boolean;
      signedAt: string | null;
      place: string | null;
    } | null;
  };
  prefill: PublicPrefill;
  proofs: PublicProof[];
  correction: { previousVersion: number; previousSubmittedAt: string } | null;
  appointment: { startsAt: string; eventTypeName: string } | null;
}

/** The 410 page of Selbstauskunft 07 (every public route answers it for an expired link). */
export interface PublicExpired {
  state: 'expired';
  maskedEmail: string | null;
  advisor: PublicAdvisor;
  organization: PublicOrganization;
  error: { code: 'LINK_EXPIRED'; message: string };
}

export interface PublicStepSaved {
  version: number;
  step: 'person' | 'income' | 'expenses' | 'assets';
  currentStep: number;
  lastSavedAt: string;
  correctionStarted: boolean;
}

export interface PublicSubmitResponse {
  state: 'submitted';
  version: number;
  pdfAvailable: true;
  submittedAt: string;
  correctionUntil: string;
}

/** Thrown for the 410 page: carries the whole page body (masked e-mail, advisor). */
export class LinkExpiredError extends Error {
  readonly page: PublicExpired;
  constructor(page: PublicExpired) {
    super(page.error.message);
    this.name = 'LinkExpiredError';
    this.page = page;
  }
}

const publicBase = (token: string) => `/api/public/self-disclosures/${encodeURIComponent(token)}`;

/** JSON call of the public form; a 410 becomes `LinkExpiredError`, every other failure an `ApiError`. */
async function publicRequest<T>(
  token: string,
  path: string,
  init: { method?: string; body?: unknown; formData?: FormData; signal?: AbortSignal } = {},
): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  let body: BodyInit | undefined;
  if (init.formData) body = init.formData;
  else if (init.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(init.body);
  }
  const response = await fetch(`${publicBase(token)}${path}`, {
    method: init.method ?? 'GET',
    headers,
    body: body ?? null,
    credentials: 'omit',
    signal: init.signal ?? null,
  });
  const text = await response.text();
  const json: unknown = text ? JSON.parse(text) : null;
  if (response.status === 410 && typeof json === 'object' && json !== null && 'state' in json) {
    throw new LinkExpiredError(json as PublicExpired);
  }
  if (!response.ok) {
    const envelope = (json as Partial<ApiErrorBody> | null)?.error ?? {
      code: ErrorCode.INTERNAL,
      message: GENERIC_ERROR_MESSAGE,
    };
    throw new ApiError(response.status, envelope);
  }
  return json as T;
}

export const publicSelfDisclosureKey = (token: string) => ['public-self-disclosure', token] as const;

export function usePublicSelfDisclosure(token: string) {
  return useQuery({
    queryKey: publicSelfDisclosureKey(token),
    queryFn: ({ signal }) => publicRequest<PublicState>(token, '', { signal }),
    retry: (count, error) =>
      !(error instanceof LinkExpiredError) && !(error instanceof ApiError && error.status < 500) && count < 1,
    staleTime: 60_000,
  });
}

export function saveStep(
  token: string,
  step: 'person' | 'income' | 'expenses' | 'assets',
  body: unknown,
): Promise<PublicStepSaved> {
  return publicRequest<PublicStepSaved>(token, `/steps/${step}`, { method: 'PATCH', body });
}

export function uploadProof(token: string, type: string, file: File): Promise<PublicProof> {
  const formData = new FormData();
  formData.set('type', type);
  formData.set('file', file, file.name);
  return publicRequest<PublicProof>(token, '/proofs', { method: 'POST', formData });
}

export function removeProof(token: string, id: string): Promise<void> {
  return publicRequest<void>(token, `/proofs/${id}`, { method: 'DELETE' });
}

export function submitSelfDisclosure(
  token: string,
  body: {
    consentCompleteness: boolean;
    consentSharing: boolean;
    propertyInfoConsent: boolean;
    signaturePng: string;
    place: string;
  },
): Promise<PublicSubmitResponse> {
  return publicRequest<PublicSubmitResponse>(token, '/submit', { method: 'POST', body });
}

export function renewLink(token: string): Promise<{ maskedEmail: string; expiresAt: string }> {
  return publicRequest<{ maskedEmail: string; expiresAt: string }>(token, '/renew', {
    method: 'POST',
    body: {},
  });
}

export function publicPdfHref(token: string): string {
  return `${publicBase(token)}/pdf`;
}
